import React, { useEffect, useState } from 'react';
import { getAllQuotas } from '../../api/quotaAPi';
import CourseList from '../../component/CourseList';
import { 
  Loader2, 
  AlertCircle, 
  FileText, 
  Calendar, 
  DollarSign, 
  Clock, 
  Users, 
  Edit,
  Search,
  Plus,
  ChevronDown,
  X,
  Filter,
  RefreshCw,
  BoxIcon
} from 'lucide-react';
import EditQuotaModal from './component/EditQuotaModal';
import CreateQuotaModal from './component/CreateQuotaModal';
import TableSkeleton from './component/TableSkeleton';

const QuotaManagement = () => {
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedQuotaId, setSelectedQuotaId] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [sortBy, setSortBy] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const fetchQuotas = async () => {
      if (!courseId) return;
      setLoading(true);
      try {
        const data = await getAllQuotas(courseId);
        setQuotas(data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch quota data');
      } finally {
        setLoading(false);
      }
    };

    fetchQuotas();
  }, [courseId, refreshFlag]);

  const handleRefresh = async () => {
    if (!courseId || refreshing) return;
    
    setRefreshing(true);
    try {
      const data = await getAllQuotas(courseId);
      setQuotas(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to refresh quota data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleEditQuota = (quotaId) => {
    setSelectedQuotaId(quotaId);
    setIsEditModalOpen(true);
  };

  const handleQuotaUpdated = () => {
    setRefreshFlag(prev => !prev);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedQuotas = [...quotas].filter(quota =>
    quota.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quota.group_id?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    if (sortBy === 'title') {
      return sortDirection === 'asc' 
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    } else if (sortBy === 'amount') {
      return sortDirection === 'asc' 
        ? a.amount - b.amount
        : b.amount - a.amount;
    } else if (sortBy === 'year') {
      return sortDirection === 'asc' 
        ? a.year - b.year
        : b.year - a.year;
    } else if (sortBy === 'duration') {
      return sortDirection === 'asc' 
        ? a.duration - b.duration
        : b.duration - a.duration;
    } else if (sortBy === 'expiry') {
      return sortDirection === 'asc' 
        ? new Date(a.fixedExpiredDate) - new Date(b.fixedExpiredDate)
        : new Date(b.fixedExpiredDate) - new Date(a.fixedExpiredDate);
    } else if (sortBy === 'group') {
      const groupA = a.group_id || '';
      const groupB = b.group_id || '';
      return sortDirection === 'asc' 
        ? groupA.toString().localeCompare(groupB.toString())
        : groupB.toString().localeCompare(groupA.toString());
    }
    return 0;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    
    return (
      <ChevronDown 
        className={`h-4 w-4 inline ml-1 transition-transform ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} 
      />
    );
  };

  return (
    <div className="min-h-screen bg-white p-1 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8  md:mb-8 mt-6 md:mt-0"> 
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex flex-row items-center gap-2"><div className='text-4xl'><BoxIcon className='size-7'/></div><div>Quota Management</div></h1>
          <p className="mt-2 text-gray-600">Configure and manage course quotas and resource allocations</p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Course Selection */}
          <div className="p-6 border-b border-gray-100 bg-white">
            <CourseList 
              courseId={courseId} 
              setCourseId={setCourseId} 
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow-sm">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
              <div className="flex justify-end mt-2">
                <button 
                  className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="px-6 py-4 border-b border-gray-100 bg-white flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              {/* Search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search quotas..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64 md:w-72 transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setSearchTerm('')}
                  >
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              {/* Filter Button (placeholder for future functionality) */}
              <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors">
                <Filter className="h-4 w-4" />
                <span>Filter</span>
              </button>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
              {/* Refresh Button */}
              <button 
                onClick={handleRefresh}
                disabled={refreshing || !courseId}
                className={`p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Create Button */}
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className={`bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm ${!courseId ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!courseId}
              >
                <Plus className="h-4 w-4" />
                <span>Create Quota</span>
              </button>
            </div>
          </div>

          {/* Data Summary */}
          <div className="px-6 py-3 bg-gray-50 text-xs font-medium text-gray-500 border-b border-gray-200">
            {sortedQuotas.length} {sortedQuotas.length === 1 ? 'quota' : 'quotas'} found
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <TableSkeleton />
            ) : sortedQuotas.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <FileText className="h-12 w-12 mb-3 text-gray-300" />
                <p className="text-lg text-gray-600 font-medium">No quota data available</p>
                <p className="text-sm mt-1 text-gray-500">
                  {searchTerm ? 'Try adjusting your search terms' : courseId ? 'Create a new quota to get started' : 'Select a course to view quotas'}
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('title')}
                    >
                      <span className="flex items-center">
                        Title
                        {getSortIcon('title')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('year')}
                    >
                      <span className="flex items-center">
                        Year
                        {getSortIcon('year')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('amount')}
                    >
                      <span className="flex items-center">
                        Amount
                        {getSortIcon('amount')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('duration')}
                    >
                      <span className="flex items-center">
                        Duration
                        {getSortIcon('duration')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('expiry')}
                    >
                      <span className="flex items-center">
                        Expiry Date
                        {getSortIcon('expiry')}
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('group')}
                    >
                      <span className="flex items-center">
                        Group ID
                        {getSortIcon('group')}
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {sortedQuotas.map((quota) => (
                    <tr key={quota.quota_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 text-gray-400 mr-2" />
                          <div className="font-medium text-gray-900">{quota.title}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          <div className="text-gray-700">{quota.year}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className='text-gray-400'>{}</span>
                          <div className="text-gray-700">{`LKR `+quota.amount}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-2" />
                          <div className="text-gray-700">{quota.duration} days</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          <div className={`${new Date(quota.fixedExpiredDate) < new Date() ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                            {formatDate(quota.fixedExpiredDate)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-gray-400 mr-2" />
                          {quota.group_id ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                              {quota.group_id}
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-50 text-gray-500 border border-gray-100">
                              Not assigned
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleEditQuota(quota.quota_id)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center transition-colors"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 text-center">
            <p>Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <EditQuotaModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        quotaId={selectedQuotaId}
        onQuotaUpdated={handleQuotaUpdated}
      />

      {/* Create Modal */}
      <CreateQuotaModal 
        isOpen={isCreateModalOpen} 
        setIsCreateModalOpen={setIsCreateModalOpen} 
        onQuotaCreated={handleQuotaUpdated}
      />
    </div>
  );
};

export default QuotaManagement;